import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Calendar, Bell, X } from "lucide-react";

const API_BASE = "https://connorvps.com/ggp-courts";
const DEFAULT_EMAIL = "connorwhcallahan@gmail.com";

// Parse "YYYY-MM-DD" without timezone shifts.
const formatDateHeader = (dateString) => {
  const [year, month, day] = dateString.split("-");
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

const getCourtAvailabilityColor = (courts) => {
  if (courts === 0) return "bg-red-100 text-red-800";
  if (courts < 3) return "bg-yellow-100 text-yellow-800";
  if (courts < 6) return "bg-green-100 text-green-800";
  return "bg-green-200 text-green-900";
};

// "07:30 AM" → "07:30"
const to24h = (timeStr) => {
  const [time, modifier] = timeStr.split(" ");
  let [h, m] = time.split(":");
  h = parseInt(h, 10);
  if (modifier === "PM" && h !== 12) h += 12;
  if (modifier === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${m}`;
};

// SF Rec & Park / Goldman Tennis Center reservation window.
// Verified SF residents: 8 days ahead. New days open daily at 12:30 PM PT.
const getMaxReservableDate = () => {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(new Date()).map((p) => [p.type, p.value]));
  const ptHour = parseInt(parts.hour, 10);
  const ptMin = parseInt(parts.minute, 10);
  const beforeRelease = ptHour < 12 || (ptHour === 12 && ptMin < 30);
  const daysAhead = beforeRelease ? 7 : 8;
  const today = new Date(`${parts.year}-${parts.month}-${parts.day}T12:00:00Z`);
  today.setUTCDate(today.getUTCDate() + daysAhead);
  return today.toISOString().slice(0, 10);
};

const CourtAvailability = forwardRef(({ onWatchCreated }, ref) => {
  const [courtData, setCourtData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection state: Set of "YYYY-MM-DD|HH:MM" strings.
  const [selected, setSelected] = useState(() => new Set());
  // Multi-select durations: subset of [30, 60, 90].
  const [durations, setDurations] = useState(() => new Set([60]));
  const [minCourts, setMinCourts] = useState(1);
  const [email, setEmail] = useState(DEFAULT_EMAIL);
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // editingId: when set, the toolbar updates an existing watch via PUT.
  const [editingId, setEditingId] = useState(null);
  // anchor: last clicked cell {date, t24} for shift-click range selection.
  const [anchor, setAnchor] = useState(null);

  // Expose `loadWatchForEdit` to parent so the Watches row's Edit button can trigger us.
  useImperativeHandle(ref, () => ({
    loadWatchForEdit(w) {
      let slots = [];
      let durs = [w.duration_minutes];
      try { slots = JSON.parse(w.slots_json) || []; } catch {}
      try {
        const parsed = JSON.parse(w.durations_json);
        if (Array.isArray(parsed) && parsed.length > 0) durs = parsed;
      } catch {}
      setSelected(new Set(slots));
      setDurations(new Set(durs));
      setEmail(w.email || DEFAULT_EMAIL);
      setLabel(w.label || "");
      setMinCourts(w.min_courts || 1);
      setEditingId(w.id);
      setSubmitError(null);
      setAnchor(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  }));

  const cancelEdit = () => {
    setEditingId(null);
    setSelected(new Set());
    setLabel("");
    setSubmitError(null);
    setAnchor(null);
  };

  useEffect(() => {
    const fetchCourtData = async () => {
      try {
        const r = await fetch(`${API_BASE}/`);
        if (!r.ok) throw new Error("Network response was not ok");
        setCourtData(await r.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCourtData();
  }, []);

  const handleCellClick = (date, t24, shiftKey, uniqueDates, uniqueTimes, maxReservable) => {
    setSubmitError(null);

    // Shift-click with existing anchor: add the entire rectangular range
    // between anchor and this cell to the selection. Skips not-yet-reservable
    // (>8d out) cells so they remain unselectable.
    if (shiftKey && anchor) {
      const dateIdx = uniqueDates.indexOf(date);
      const anchorDateIdx = uniqueDates.indexOf(anchor.date);
      const timeIdx = uniqueTimes.findIndex((t) => to24h(t) === t24);
      const anchorTimeIdx = uniqueTimes.findIndex((t) => to24h(t) === anchor.t24);
      if (dateIdx === -1 || anchorDateIdx === -1 || timeIdx === -1 || anchorTimeIdx === -1) return;

      const [d0, d1] = [Math.min(dateIdx, anchorDateIdx), Math.max(dateIdx, anchorDateIdx)];
      const [t0, t1] = [Math.min(timeIdx, anchorTimeIdx), Math.max(timeIdx, anchorTimeIdx)];

      setSelected((prev) => {
        const next = new Set(prev);
        for (let di = d0; di <= d1; di++) {
          const d = uniqueDates[di];
          if (d > maxReservable) continue;
          for (let ti = t0; ti <= t1; ti++) {
            next.add(`${d}|${to24h(uniqueTimes[ti])}`);
          }
        }
        return next;
      });
      setAnchor({ date, t24 });
      return;
    }

    // Regular click: toggle this cell, update anchor.
    setSelected((prev) => {
      const key = `${date}|${t24}`;
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setAnchor({ date, t24 });
  };

  const clearSelection = () => {
    setSelected(new Set());
    setAnchor(null);
  };

  const toggleDuration = (d) => {
    setDurations((prev) => {
      const next = new Set(prev);
      if (next.has(d)) {
        // Keep at least one duration selected.
        if (next.size > 1) next.delete(d);
      } else {
        next.add(d);
      }
      return next;
    });
  };

  const saveWatch = async () => {
    if (selected.size === 0 || durations.size === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const url = editingId ? `${API_BASE}/watches/${editingId}` : `${API_BASE}/watches`;
      const method = editingId ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim() || DEFAULT_EMAIL,
          slots: Array.from(selected),
          durations: Array.from(durations).sort((a, b) => a - b),
          min_courts: parseInt(minCourts, 10) || 1,
          label: label.trim() || null,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      clearSelection();
      setLabel("");
      setEditingId(null);
      if (onWatchCreated) onWatchCreated();
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-2xl font-bold">Loading...</div>
      </div>
    );
  }
  if (error) {
    return <div className="flex justify-center items-center h-screen text-red-600">Error: {error}</div>;
  }
  if (!courtData) return null;

  const uniqueDates = Array.from(new Set(courtData.data.map((it) => it.Date))).sort();
  const uniqueTimes = Array.from(new Set(courtData.data.map((it) => it.Time))).sort((a, b) =>
    to24h(a).localeCompare(to24h(b))
  );

  const dayTimeMap = {};
  courtData.data.forEach(({ Date, Time, AvailableCourts }) => {
    if (!dayTimeMap[Date]) dayTimeMap[Date] = {};
    dayTimeMap[Date][Time] = AvailableCourts;
  });

  const maxReservable = getMaxReservableDate();
  const sortedDurations = Array.from(durations).sort((a, b) => a - b);
  const minSlots = sortedDurations[0] / 30;
  const maxSlots = sortedDurations[sortedDurations.length - 1] / 30;
  const slotHint =
    minSlots === maxSlots
      ? `needs ${minSlots} consecutive slot${minSlots !== 1 ? "s" : ""}`
      : `needs ${minSlots}–${maxSlots} consecutive slots`;

  return (
    <div className="max-w-6xl mx-auto p-4 pb-2">
      <div className="flex items-center mb-2">
        <Calendar className="text-gray-600 mr-2" />
        <h2 className="text-2xl font-bold">
          Courts Available — Lisa &amp; Douglas Goldman Tennis Center
        </h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Click cells to select. Shift-click to fill a rectangular range. A reminder toolbar appears below.
      </p>

      <div className="overflow-x-auto bg-white shadow rounded-lg">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="p-2 text-left border-b border-gray-200">Time</th>
              {uniqueDates.map((date) => {
                const notYet = date > maxReservable;
                return (
                  <th
                    key={date}
                    className={`p-2 text-center border-b border-l border-gray-200 ${
                      notYet ? "italic text-gray-400 font-normal" : ""
                    }`}
                    title={notYet ? "Not yet reservable (>8 days out)" : undefined}
                  >
                    {formatDateHeader(date)}
                    {notYet && (
                      <div className="text-[10px] font-normal text-gray-400 mt-0.5">not yet bookable</div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {uniqueTimes.map((time) => {
              const t24 = to24h(time);
              return (
                <tr key={time} className="border-b last:border-b-0 border-gray-200">
                  <td className="p-2 font-medium whitespace-nowrap">{time}</td>
                  {uniqueDates.map((date) => {
                    const courts = dayTimeMap[date]?.[time] ?? 0;
                    const notYet = date > maxReservable;
                    const key = `${date}|${t24}`;
                    const isSelected = selected.has(key);

                    if (notYet) {
                      return (
                        <td
                          key={date}
                          className="p-2 text-center border-l border-gray-200 italic text-gray-400 bg-gray-50"
                          title="Not yet reservable"
                        >
                          {courts}
                        </td>
                      );
                    }
                    return (
                      <td
                        key={date}
                        onClick={(e) =>
                          handleCellClick(date, t24, e.shiftKey, uniqueDates, uniqueTimes, maxReservable)
                        }
                        className={`p-2 text-center border-l border-gray-200 font-bold cursor-pointer select-none transition-shadow ${getCourtAvailabilityColor(
                          courts
                        )} ${isSelected ? "ring-2 ring-blue-500 ring-inset" : "hover:opacity-80"}`}
                      >
                        {courts}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-gray-500 mt-2">
        Italic / greyed columns are more than 8 days out and not yet reservable.
        New days open daily at 12:30 PM PT (verified SF residents, per SF Rec &amp; Park).
      </div>

      <div className="text-center text-gray-500 mt-2 text-xs">
        Last Updated: {new Date(courtData.lastUpdated).toLocaleString()}
      </div>

      {selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50">
          <div className="max-w-6xl mx-auto p-3 flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2 mr-2">
              <Bell className="text-blue-600" size={18} />
              <span className="font-semibold">
                {editingId ? `Editing reminder #${editingId} · ` : ""}
                {selected.size} cell{selected.size !== 1 ? "s" : ""} selected
              </span>
              {editingId ? (
                <button
                  onClick={cancelEdit}
                  className="text-xs text-gray-500 hover:text-gray-800 underline ml-1"
                >
                  cancel edit
                </button>
              ) : (
                <button
                  onClick={clearSelection}
                  title="Clear selection"
                  className="text-gray-400 hover:text-gray-700 ml-1"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <div>
              <label className="block text-xs uppercase text-gray-500 mb-1">Durations (multi)</label>
              <div className="flex border border-gray-300 rounded overflow-hidden">
                {[30, 60, 90].map((d) => {
                  const on = durations.has(d);
                  return (
                    <button
                      key={d}
                      onClick={() => toggleDuration(d)}
                      className={`px-3 py-1 text-sm ${
                        on
                          ? "bg-blue-600 text-white font-semibold"
                          : "bg-white text-gray-700 hover:bg-gray-100"
                      }`}
                      title={on ? "Click to remove" : "Click to add"}
                    >
                      {d}m
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-gray-400 mt-0.5">{slotHint}</div>
            </div>

            <div>
              <label className="block text-xs uppercase text-gray-500 mb-1">Min courts</label>
              <input
                type="number" min="1" value={minCourts}
                onChange={(e) => setMinCourts(e.target.value)}
                className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>

            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs uppercase text-gray-500 mb-1">Email</label>
              <input
                type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>

            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs uppercase text-gray-500 mb-1">Label (optional)</label>
              <input
                type="text" value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. weekend morning"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>

            <div className="flex flex-col items-end">
              {submitError && <span className="text-red-600 text-xs mb-1">{submitError}</span>}
              <button
                onClick={saveWatch}
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded text-sm disabled:opacity-50"
              >
                {submitting ? "Saving…" : editingId ? "Update reminder" : "Save reminder"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

CourtAvailability.displayName = "CourtAvailability";

export default CourtAvailability;
